import React, { useState, useEffect, useRef } from "react";
import { formatDateTime } from "../utils/FormatDateTime";
import ShowModal from "./Hud/ShowModal";
import { BiChevronLeft, BiChevronRight, BiExpand, BiLinkExternal, BiZoomIn, BiZoomOut, BiRotateLeft, BiRotateRight, BiReset, BiDownload, BiFullscreen, BiExitFullscreen, BiInfoCircle } from "react-icons/bi";
import { getUserRole } from '../utils/localStoarege';
import { toast } from "react-toastify";
import { localToken } from "../constants/apis/new_hud_apis";
import axios from "axios";

const PatientPhotoGalleryApiComponent = ({
    apiEndpoint,
    imageToDisplay,
    componentToDisplay,
    activeIndex = 0,
    token = localToken,
    className,
    isOperator = getUserRole() === 'operator',
}) => {
    const [images, setImages] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(activeIndex);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nextPageUrl, setNextPageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [rotation, setRotation] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const thumbnailContainerRef = useRef(null);
    const thumbnailRefs = useRef([]);
    const imageContainerRef = useRef(null);
    const modalContainerRef = useRef(null);
    const imageRef = useRef(null);

    const fetchImages = async (url = apiEndpoint) => {
        if (!url || loading) return;
        setLoading(true);
        try {
            const config = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Token ${token}`,
                },
            };
            const secureUrl = url.startsWith('http://') ? url.replace('http://', 'https://') : url;
            const data = (await axios.get(secureUrl, config)).data;
            if (Array.isArray(data.results)) {
                setImages(prev => {
                    // Filter out duplicates by id
                    const existingIds = new Set(prev.map(img => img.id));
                    const newImages = data.results.filter(img => !existingIds.has(img.id));
                    return [...prev, ...newImages];
                });
                setNextPageUrl(data.next);
            }
        } catch (err) {
            toast.error("Failed to load images");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const openModal = () => {
        setImages([]);
        setNextPageUrl(null);
        setCurrentIndex(activeIndex);
        setZoomLevel(1.0);
        setRotation(0);
        setPanOffset({ x: 0, y: 0 });
        setIsModalOpen(true);
        fetchImages(apiEndpoint);
    };

    const closeModal = () => setIsModalOpen(false);

    const handleNext = () => {
        if (images.length === 0) return;
        setCurrentIndex(prev => {
            const nextIndex = (prev + 1);
            // Prefetch more if near end
            if (nextIndex >= images.length - 2 && nextPageUrl) {
                fetchImages(nextPageUrl);
            }
            setZoomLevel(1.0);
            setRotation(0);
            setPanOffset({ x: 0, y: 0 });
            return nextIndex % images.length;
        });
    };

    const handlePrev = () => {
        if (images.length === 0) return;
        setCurrentIndex(prev => {
            setZoomLevel(1.0);
            setRotation(0);
            setPanOffset({ x: 0, y: 0 });
            return (prev - 1 + images.length) % images.length;
        });
    };

    const handleThumbnailClick = (index) => {
        setCurrentIndex(index);
        setZoomLevel(1.0);
        setRotation(0);
        setPanOffset({ x: 0, y: 0 });
        // If user clicks on the last or second last thumbnail and more images are available, fetch more
        if ((index >= images.length - 2) && nextPageUrl) {
            fetchImages(nextPageUrl);
        }
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(5.0, prev + 0.2));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(1.0, prev - 0.2));
    };

    const handleRotateLeft = () => {
        setRotation(prev => prev - 90);
    };

    const handleRotateRight = () => {
        setRotation(prev => prev + 90);
    };

    const handleResetView = () => {
        setZoomLevel(1.0);
        setRotation(0);
        setPanOffset({ x: 0, y: 0 });
        toast.success("View reset");
    };

    const handleDownload = () => {
        if (images.length > 0 && images[currentIndex]?.photo) {
            const link = document.createElement('a');
            link.href = images[currentIndex].photo;
            link.download = `image-${currentIndex + 1}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("Download started");
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            modalContainerRef.current?.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };

    const handleWheel = (e) => {
        if (!isModalOpen || images.length === 0 || loading) return;
        e.preventDefault();

        const container = imageContainerRef.current;
        const rect = container.getBoundingClientRect();

        // Mouse position relative to container center
        const containerCenterX = rect.width / 2;
        const containerCenterY = rect.height / 2;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const delta = e.deltaY > 0 ? -0.2 : 0.2;

        setZoomLevel(prev => {
            const oldZoom = prev;
            const newZoom = Math.max(1.0, Math.min(5.0, prev + delta));

            if (newZoom === 1.0) {
                setPanOffset({ x: 0, y: 0 });
                return newZoom;
            }

            // Calculate the point on the image that's under the cursor
            // Before zoom: imagePoint = (mousePos - panOffset - center) / oldZoom + center
            const imagePointX = (mouseX - panOffset.x - containerCenterX) / oldZoom + containerCenterX;
            const imagePointY = (mouseY - panOffset.y - containerCenterY) / oldZoom + containerCenterY;

            // After zoom: newPanOffset = mousePos - (imagePoint - center) * newZoom - center
            setPanOffset({
                x: mouseX - (imagePointX - containerCenterX) * newZoom - containerCenterX,
                y: mouseY - (imagePointY - containerCenterY) * newZoom - containerCenterY
            });

            return newZoom;
        });
    };

    const handleMouseDown = (e) => {
        if (zoomLevel > 1.0) {
            setIsDragging(true);
            const container = imageContainerRef.current;
            const rect = container.getBoundingClientRect();
            const containerCenterX = rect.width / 2;
            const containerCenterY = rect.height / 2;
            setDragStart({
                x: e.clientX - panOffset.x - containerCenterX,
                y: e.clientY - panOffset.y - containerCenterY
            });
            e.preventDefault();
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoomLevel > 1.0) {
            const container = imageContainerRef.current;
            const rect = container.getBoundingClientRect();
            const containerCenterX = rect.width / 2;
            const containerCenterY = rect.height / 2;
            setPanOffset({
                x: e.clientX - dragStart.x - containerCenterX,
                y: e.clientY - dragStart.y - containerCenterY
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handlePopOut = () => {
        if (images.length > 0 && images[currentIndex]?.photo) {
            window.open(images[currentIndex].photo, '_blank', 'noopener,noreferrer');
        }
    };

    const handleKeyDown = (e) => {
        if (loading) {
            // Prevent right arrow and right click when loading
            if (e.key === "ArrowRight") {
                e.preventDefault();
                return;
            }
        }
        if (e.key === "ArrowRight") {
            handleNext();
        } else if (e.key === "ArrowLeft") {
            handlePrev();
        } else if (e.key === "Escape") {
            if (document.fullscreenElement) {
                document.exitFullscreen();
                setIsFullscreen(false);
            } else {
                closeModal();
            }
        } else if (e.key === "+" || e.key === "=") {
            handleZoomIn();
        } else if (e.key === "-" || e.key === "_") {
            handleZoomOut();
        } else if (e.key === "r" || e.key === "R") {
            handleResetView();
        } else if (e.key === "f" || e.key === "F") {
            toggleFullscreen();
        } else if (e.key === "d" || e.key === "D") {
            handleDownload();
        }
    };

    // Scroll to current thumbnail
    useEffect(() => {
        if (isModalOpen && thumbnailRefs.current[currentIndex]) {
            setTimeout(() => {
                thumbnailRefs.current[currentIndex]?.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "nearest"
                });
            }, 100);
        }
    }, [currentIndex, isModalOpen]);

    // Add/remove key listener
    useEffect(() => {
        if (isModalOpen) window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isModalOpen, images.length, currentIndex]);

    // Lazy load more on thumbnail scroll
    useEffect(() => {
        const container = thumbnailContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            if (scrollLeft + clientWidth >= scrollWidth - 50 && nextPageUrl) {
                fetchImages(nextPageUrl);
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [nextPageUrl]);

    // Add wheel event listener for zoom
    useEffect(() => {
        const container = imageContainerRef.current;
        if (isModalOpen && container) {
            container.addEventListener("wheel", handleWheel, { passive: false });
        }
        return () => {
            if (container) {
                container.removeEventListener("wheel", handleWheel);
            }
        };
    }, [isModalOpen, images.length, loading, panOffset, zoomLevel]);

    // Add mouse event listeners for dragging
    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragStart, zoomLevel]);

    // Reset zoom, rotation and pan when image changes
    useEffect(() => {
        setZoomLevel(1.0);
        setRotation(0);
        setPanOffset({ x: 0, y: 0 });
    }, [currentIndex]);

    return (
        <div
            className="relative w-full flex flex-col items-center"
            onContextMenu={e => {
                if (loading) {
                    e.preventDefault();
                }
            }}
        >
            {
                componentToDisplay ? (
                    <div onClick={openModal}>
                        {componentToDisplay}
                    </div>
                ) :
                    imageToDisplay && (
                        <div
                            className="relative cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105 group"
                            onClick={openModal}
                        >
                            <img
                                src={imageToDisplay}
                                alt="Image"
                                className={`${className} rounded-full shadow-md`}
                                style={isOperator ? { filter: 'blur(8px) brightness(0.7)' } : {}}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-opacity duration-300 rounded-full">
                                <BiExpand className="text-white opacity-0 group-hover:opacity-100 w-12 h-12" />
                            </div>
                        </div>
                    )
            }

            <ShowModal
                contentLabel="Photo Gallery"
                modalWidth="95%"
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                componentToPass={
                    <div ref={modalContainerRef} className="w-full max-w-full mx-auto p-4 rounded-lg flex flex-col h-full">
                        <div
                            ref={imageContainerRef}
                            className="relative w-full h-[70vh] mb-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg overflow-hidden cursor-zoom-in shadow-xl"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center h-full text-white">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-3"></div>
                                        <p>Loading images...</p>
                                    </div>
                                </div>
                            ) : images.length > 0 ? (
                                <div
                                    className="relative w-full h-full overflow-hidden"
                                    onMouseDown={handleMouseDown}
                                    style={{ cursor: zoomLevel > 1.0 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
                                >
                                    <img
                                        ref={imageRef}
                                        src={images[currentIndex]?.photo}
                                        alt={`Image ${currentIndex + 1}`}
                                        className="object-contain transition-transform duration-200 select-none"
                                        draggable={false}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: `translate(calc(-50% + ${panOffset.x}px), calc(-50% + ${panOffset.y}px)) scale(${zoomLevel}) rotate(${rotation}deg)`,
                                            transformOrigin: 'center center',
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            ...(isOperator ? { filter: 'blur(8px) brightness(0.7)' } : {})
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-full text-white">No photos to show</div>
                            )}

                            {/* Top Control Bar */}
                            {images.length > 0 && !loading && (
                                <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-20">
                                    {/* Left side - Zoom and Rotation controls */}
                                    <div className="flex gap-2 bg-black/70 backdrop-blur-sm rounded-full p-2">
                                        <button
                                            onClick={handleZoomOut}
                                            disabled={zoomLevel <= 1.0}
                                            className="p-2 rounded-full hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            aria-label="Zoom out"
                                            title="Zoom Out (-)"
                                        >
                                            <BiZoomOut className="w-5 h-5 text-white" />
                                        </button>
                                        <div className="px-3 flex items-center text-white text-sm font-medium min-w-[60px] justify-center">
                                            {Math.round(zoomLevel * 100)}%
                                        </div>
                                        <button
                                            onClick={handleZoomIn}
                                            disabled={zoomLevel >= 5.0}
                                            className="p-2 rounded-full hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            aria-label="Zoom in"
                                            title="Zoom In (+)"
                                        >
                                            <BiZoomIn className="w-5 h-5 text-white" />
                                        </button>
                                        <div className="w-px h-6 bg-white/30 self-center"></div>
                                        <button
                                            onClick={handleRotateLeft}
                                            className="p-2 rounded-full hover:bg-white/20 transition-colors"
                                            aria-label="Rotate left"
                                            title="Rotate Left"
                                        >
                                            <BiRotateLeft className="w-5 h-5 text-white" />
                                        </button>
                                        <button
                                            onClick={handleRotateRight}
                                            className="p-2 rounded-full hover:bg-white/20 transition-colors"
                                            aria-label="Rotate right"
                                            title="Rotate Right"
                                        >
                                            <BiRotateRight className="w-5 h-5 text-white" />
                                        </button>
                                        <button
                                            onClick={handleResetView}
                                            className="p-2 rounded-full hover:bg-white/20 transition-colors"
                                            aria-label="Reset view"
                                            title="Reset View (R)"
                                        >
                                            <BiReset className="w-5 h-5 text-white" />
                                        </button>
                                    </div>

                                    {/* Right side - Action buttons */}
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setShowHelp(!showHelp)}
                                            className="p-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm transition-colors relative"
                                            aria-label="Keyboard shortcuts"
                                            title="Keyboard Shortcuts"
                                        >
                                            <BiInfoCircle className="w-5 h-5 text-white" />
                                        </button>
                                        <button
                                            onClick={handleDownload}
                                            className="p-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm transition-colors"
                                            aria-label="Download image"
                                            title="Download (D)"
                                        >
                                            <BiDownload className="w-5 h-5 text-white" />
                                        </button>
                                        <button
                                            onClick={handlePopOut}
                                            className="p-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm transition-colors"
                                            aria-label="Open in new window"
                                            title="Open in New Window"
                                        >
                                            <BiLinkExternal className="w-5 h-5 text-white" />
                                        </button>
                                        <button
                                            onClick={toggleFullscreen}
                                            className="p-3 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm transition-colors"
                                            aria-label="Toggle fullscreen"
                                            title="Fullscreen (F)"
                                        >
                                            {isFullscreen ? (
                                                <BiExitFullscreen className="w-5 h-5 text-white" />
                                            ) : (
                                                <BiFullscreen className="w-5 h-5 text-white" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Help Tooltip */}
                            {showHelp && (
                                <div className="absolute top-20 right-4 bg-black/90 backdrop-blur-sm text-white p-4 rounded-lg shadow-2xl z-30 max-w-xs">
                                    <h3 className="font-bold text-sm mb-3 border-b border-white/30 pb-2">Keyboard Shortcuts</h3>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between"><span className="text-gray-300">Next Image:</span><kbd className="bg-white/20 px-2 py-1 rounded">→</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Previous Image:</span><kbd className="bg-white/20 px-2 py-1 rounded">←</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Zoom In:</span><kbd className="bg-white/20 px-2 py-1 rounded">+</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Zoom Out:</span><kbd className="bg-white/20 px-2 py-1 rounded">-</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Reset View:</span><kbd className="bg-white/20 px-2 py-1 rounded">R</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Download:</span><kbd className="bg-white/20 px-2 py-1 rounded">D</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Fullscreen:</span><kbd className="bg-white/20 px-2 py-1 rounded">F</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Close:</span><kbd className="bg-white/20 px-2 py-1 rounded">ESC</kbd></div>
                                        <div className="flex justify-between"><span className="text-gray-300">Scroll:</span><span className="text-gray-400 text-[10px]">Zoom In/Out</span></div>
                                    </div>
                                </div>
                            )}

                            {/* Navigation Buttons - Made larger and more visible */}
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={handlePrev}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 p-4 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-sm transition-all hover:scale-110 shadow-xl"
                                        aria-label="Previous image"
                                    >
                                        <BiChevronLeft className="w-8 h-8 text-white" />
                                    </button>

                                    <button
                                        onClick={handleNext}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 p-4 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-sm transition-all hover:scale-110 shadow-xl"
                                        aria-label="Next image"
                                    >
                                        <BiChevronRight className="w-8 h-8 text-white" />
                                    </button>
                                </>
                            )}

                            {/* Counter and date overlay - Enhanced visibility */}
                            {images.length > 0 && !loading && (
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent backdrop-blur-sm text-white p-4 flex justify-between items-center">
                                    <span className="font-semibold text-lg bg-white/10 px-3 py-1 rounded-full">
                                        {currentIndex + 1} / {images.length}
                                    </span>
                                    <span className="text-sm bg-white/10 px-3 py-1 rounded-full">
                                        {images[currentIndex]?.created_at
                                            ? `📅 ${formatDateTime(images[currentIndex].created_at)}`
                                            : "No date available"}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Enhanced Thumbnails with better scrolling */}
                        {images.length > 1 && (
                            <div
                                className="relative px-4 py-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl shadow-inner"
                                ref={thumbnailContainerRef}
                            >
                                <div className="flex gap-3 overflow-x-auto py-3 px-2 max-w-full scrollbar-thin scrollbar-thumb-blue-400 scrollbar-track-gray-300 scrollbar-rounded">
                                    {images.map((img, idx) => (
                                        <button
                                            key={img.id || idx}
                                            ref={(el) => (thumbnailRefs.current[idx] = el)}
                                            onClick={() => handleThumbnailClick(idx)}
                                            className={`flex-none transition-all duration-300 rounded-lg ${currentIndex === idx
                                                ? 'ring-4 ring-blue-500 scale-110 z-10 shadow-lg shadow-blue-500/50'
                                                : 'hover:ring-2 hover:ring-blue-400 hover:scale-105 opacity-70 hover:opacity-100'
                                                }`}
                                        >
                                            <img
                                                src={img.photo}
                                                alt={`Thumbnail ${idx + 1}`}
                                                className="w-24 h-24 object-cover rounded-lg"
                                                style={isOperator ? { filter: 'blur(4px) brightness(0.7)' } : {}}
                                            />
                                            {currentIndex === idx && (
                                                <div className="absolute inset-0 bg-blue-500/20 rounded-lg pointer-events-none"></div>
                                            )}
                                        </button>
                                    ))}
                                </div>

                                {/* Scroll indicators if there are many images */}
                                {images.length > 5 && (
                                    <>
                                        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-gray-200 to-transparent pointer-events-none rounded-l-xl" />
                                        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-gray-200 to-transparent pointer-events-none rounded-r-xl" />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                }
            />
        </div>
    );
};

export default PatientPhotoGalleryApiComponent;